/**
 * Actions on My Today / Today "Needs action" items, shared by the floating
 * panel and the Today page so both clear items the same way.
 */
import { addDoc, collection, deleteField, doc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';
import type { UrgentItem } from '../components/myTodayUrgent';

/** Ignore reasons for a closed lead that came back on WhatsApp. */
export const IGNORE_REASONS: Array<{ key: string; label: string; section: 'ignore' | 'notLead'; hint?: string }> = [
  { key: 'browsing', label: 'Just browsing / not serious', section: 'ignore', hint: 'Polite note if they booked a visit in the chat' },
  { key: 'customer', label: 'Already a customer', section: 'ignore' },
  { key: 'broker', label: 'Broker', section: 'ignore' },
  { key: 'wrong-number', label: 'Wrong number', section: 'ignore', hint: 'Stops automated WhatsApp' },
  // Not a lead: never messaged automatically again; shows under Messages → Not a lead.
  { key: 'not-lead-pitch', label: 'Sales pitch / agency', section: 'notLead' },
  { key: 'not-lead-supplier', label: 'Supplier / vendor', section: 'notLead' },
  { key: 'not-lead-job', label: 'Job seeker', section: 'notLead' },
  { key: 'not-lead-other', label: 'Other', section: 'notLead' },
];

export const IGNORE_SECTIONS: Array<{ key: 'ignore' | 'notLead'; title: string; note?: string }> = [
  { key: 'ignore', title: 'Ignore' },
  { key: 'notLead', title: 'Not a lead', note: 'stops automated WhatsApp' },
];

/** Done: call-back, waiting customer, re-book task, old visit request. */
export const markItemDone = async (db: Firestore, item: UrgentItem, by: string | null) => {
  const completedAtMs = Date.now();
  if (item.kind === 'visitRequest' && item.leadId) {
    await updateDoc(doc(db, 'leads', item.leadId), {
      'whatsappVisitRequest.status': 'done',
      'whatsappVisitRequest.doneAt': serverTimestamp(),
      'whatsappVisitRequest.doneBy': by,
    });
  }
  if (item.kind === 'visitTask' && item.leadId) {
    await updateDoc(doc(db, 'leads', item.leadId), {
      'visitFollowUpTask.status': 'done',
      'visitFollowUpTask.doneAt': serverTimestamp(),
      'visitFollowUpTask.doneBy': by,
    });
  }
  if (item.kind === 'visitMessageFailure' && item.leadId) {
    await updateDoc(doc(db, 'leads', item.leadId), {
      'whatsappVisitDeliveryTask.status': 'done',
      'whatsappVisitDeliveryTask.doneAt': serverTimestamp(),
      'whatsappVisitDeliveryTask.doneBy': by,
    });
  }
  if (item.kind === 'callback' && item.leadId) {
    await updateDoc(doc(db, 'leads', item.leadId), {
      'websiteRequest.status': 'completed',
      'websiteRequest.completedAt': serverTimestamp(),
      'websiteRequest.completedBy': by,
    });
    await addDoc(collection(db, 'leads', item.leadId, 'activities'), {
      type: 'internal', event: 'whatsapp-callback-done', notes: ['Call-back request marked done.'], byEmail: by, at: serverTimestamp(),
    });
  }
  if (item.phone10 && item.kind !== 'visitMessageFailure') {
    await setDoc(doc(db, 'whatsappContactState', item.phone10), {
      ...(item.kind === 'human' ? { needsHuman: false, needsHumanHandledAt: serverTimestamp(), needsHumanHandledBy: by } : {}),
      openRequest: deleteField(),
    }, { merge: true });
    // The task and the unread chat often come from the same customer tap.
    // Mark messages already present when Done was clicked as read; a later
    // inbound message remains unread and can create a fresh item.
    try {
      const snapshot = await getDocs(query(
        collection(db, 'whatsappMessages'),
        where('contactPhone', '==', `91${item.phone10}`),
        orderBy('createdAt', 'desc'),
        limit(100),
      ));
      const unread = snapshot.docs.filter((message) => {
        const data = message.data();
        const receivedAtMs = typeof data.createdAt?.toMillis === 'function' ? data.createdAt.toMillis() : 0;
        return data.direction === 'inbound' && !data.readAt && receivedAtMs <= completedAtMs;
      });
      if (unread.length) {
        const batch = writeBatch(db);
        unread.forEach((message) => batch.update(message.ref, {
          readAt: serverTimestamp(),
          readBy: by,
        }));
        await batch.commit();
      }
    } catch (error) {
      console.warn('Task completed, but its WhatsApp messages could not be marked read', error);
    }
  }
};

/** Reopen (optionally taking the lead) or Ignore with a reason. */
export const resolveReengagement = async (leadId: string, decision: string, assignToMe = false) =>
  httpsCallable(getFunctions(app), 'whatsappResolveReengagement')({ leadId, decision, assignToMe });

/** A lead name that says nothing (".", "-", digits): show the phone instead. */
export const displayName = (name: string, phone10: string) => {
  const n = String(name || '').trim();
  if (n.length >= 2 && /[a-zऀ-෿]/i.test(n)) return n;
  return phone10 ? `+91 ${phone10.slice(0, 5)} ${phone10.slice(5)}` : n || 'Customer';
};
